import React, { useCallback } from "react";
import { AccordionDetails } from "@material-ui/core";
import produce from "immer";
import type { FC } from "react";
import { useStyles } from "./Control.styles";
import { IControlProps } from "./Control.types";
import { createControl } from "./Control.factory";
import { IBulkExportContextState, useBulkExportContext } from "../../../../providers";
import { BulkExportFormat } from "../../../../types";
import { virtualConfiguration } from "../../BulkExportStepper.constants";
import { IBulkExportContextActions } from "../../../../providers/BulkExportProvider/BulkExportProvider.types";

export const Control: FC<IControlProps> = (props) => {
  const { hierarchyIdentifier, type, items, allSteps, item, bulkId, favoriteBulkItemByQuery, isSubscriptionBulk } = props;
  const classes = useStyles();

  const context = useBulkExportContext();
  const { setDimensionCurrentFavourite, setDimension: setDimensions, setFormatCurrentFavourite, setFormat: setFormats } = context?.actions as IBulkExportContextActions;
  const { dataSourceId , currentFavouriteSettings, dimensions } = context?.state as IBulkExportContextState;
  const isBulkId = Boolean(bulkId);
  const isFavouriteBulkByQuery = Boolean(favoriteBulkItemByQuery);
  
  const selectedDataSourceId = isBulkId ? currentFavouriteSettings?.mark?.id! : dataSourceId!;

  let contextDimensions;
  if (isBulkId || isFavouriteBulkByQuery) {
    contextDimensions = currentFavouriteSettings?.mark?.parameters?.dimensions;
  } else {
    contextDimensions = dimensions;
  }

  const favouriteFormat = currentFavouriteSettings?.mark?.parameters?.format!;

  const formatMap = {
    0: BulkExportFormat.XLSX,
    1: BulkExportFormat.CSV,
  };

  let format;
  if (item || isFavouriteBulkByQuery) {
    format = formatMap[favouriteFormat as number] || BulkExportFormat.XLSX;
  } else {
    format = context?.state?.format!;
  }

  let setDimension;
  if ((item && !isSubscriptionBulk) || isFavouriteBulkByQuery) {
    setDimension = setDimensionCurrentFavourite;
  } else {
    setDimension = setDimensions;
  }

  const setFormat = item || isFavouriteBulkByQuery ? setFormatCurrentFavourite : setFormats;
  const currentContextDimension = contextDimensions?.find((d) => d.id === hierarchyIdentifier);

  const onChangeHandler = useCallback(
    (selection: (string | number)[]) => {
      if (virtualConfiguration.identifier === hierarchyIdentifier) {
        const currentSelection = selection?.map((sel) => `${(sel as string).split(".")[0]}.${(sel as string).split(".")[1]}`) || [];
        if (currentSelection && currentSelection?.length > 0 && setDimension) {
          currentSelection.forEach((sel) => {
            const dim = contextDimensions.find((dim) => dim.id === sel);
            if (dim) {
              setDimension({ ...dim, fieldName: `${(sel as string).split(".")[0]} ${(sel as string).split(".")[1]}` });
            }
          });
        }
        if (currentSelection.length > 0) {
          contextDimensions.forEach((dim) => {
            if (!currentSelection.includes(dim.id)) {
              setDimension({ ...dim, fieldName: "" });
            }
          });
        }
      }

      const finalDimension = produce(
        currentContextDimension ?? {
          id: hierarchyIdentifier,
          selection,
          isSelected: true,
          fieldName: "",
        },
        (draft) => {
          draft.selection = selection;
        }
      );
      setDimension?.(finalDimension as any);
    },
    [setDimension, currentContextDimension, contextDimensions]
  );

  const selection = currentContextDimension?.selection ?? [];
  const control = createControl(
    selectedDataSourceId,
    type,
    items,
    allSteps,
    selection,
    onChangeHandler,
    format,
    setFormat!,
    contextDimensions ?? [],
    isSubscriptionBulk
  );
  return <AccordionDetails classes={{ root: classes.details }}>{control}</AccordionDetails>;
};
